import React from 'react';
import PropTypes from 'prop-types';
import { map } from 'lodash';
import { InviteShareBtn } from '../elements-buttons/invite-share-button';
import { FormattedMessage, FormattedNumber, injectIntl, intlShape } from 'react-intl';
import SharedIntlProviderHOC from 'shared-components/hocs/sharedIntlProviderHOC';

const ReferralsMain = ({
  referralCount,
  referralBalanceAmount,
  referralBalanceError,
  entitlementId,
  entitlementError,
  SSOid,
  entitlementDescription,
  creditValue,
  discountValue,
  minimumBookingSubtotal,
  intl
}) => {
  const howItWorksData = [
    {
      icons: ['instagram', 'youtube', 'tiktok'],
      header: intl.formatMessage({ id: 'referrals.referrals_main.share_anywhere', defaultMessage: 'Share anywhere' }),
      body: intl.formatMessage({ id: 'referrals.referrals_main.get_creatives', defaultMessage: 'Get creative with how you share. Text your link to friends, create an Instagram story, or shout it out on TikTok.' })
    },
    {
      icons: ['dollar'],
      header: intl.formatMessage({ id: 'referrals.referrals_main.earn_up', defaultMessage: 'Earn up to {value}' }, { value: intl.formatNumber(5000, { style: 'currency', currency: 'USD', minimumFractionDigits: 0 === 5000 % 1 ? 0 : 2, maximumFractionDigits: 0 === 5000 % 1 ? 0 : 2 }) }),
      body: intl.formatMessage({ id: 'referrals.referrals_main.stack_credits', defaultMessage: 'Stack those credits. You can earn up to {value} through your referral link.' }, { value: intl.formatNumber(5000, { style: 'currency', currency: 'USD', minimumFractionDigits: 0 === 5000 % 1 ? 0 : 2, maximumFractionDigits: 0 === 5000 % 1 ? 0 : 2 }) }),
      link: true
    },
    {
      icons: ['calendar'],
      header: intl.formatMessage({ id: 'referrals.referrals_main.good_for_booking', defaultMessage: 'Good for any US booking' }),
      body: intl.formatMessage({ id: 'referrals.referrals_main.use_peerspace_credit', defaultMessage: 'Use Peerspace Credit to book any space in the United States.' }),
      link: true
    }
  ];

  const zendeskLocale = SharedIntlProviderHOC.getZendeskLocale();
  const prefix = SharedIntlProviderHOC.getUrlPrefix();
  const urlPrefix = prefix ? `/${prefix}` : '';

  return (
    <div className="referrals-main">
      <div className="page ps-container-lg-margin ps-container-fixed">
        <div className="ps-row">
          <div className='col-xs-12 col-md-6 order-xs-first order-md-last'>
            <div className="referral-image"></div>
          </div>
          
          <div className="col-xs-12 col-md-6 order-xs-last order-md-first">
            <div className="h1 text-xs-center text-sm-left">{entitlementDescription}</div>
            <div className="text-gray-800 margin-xl-bottom text-xs-center text-sm-left">
              <FormattedMessage
                id='referrals.referrals_main.share_link'
                defaultMessage="Share your referral link to give a {discount} discount to friends and followers. When they book their first space, you'll earn a {credit} credit."
                values={{
                  discount: intl.formatNumber(discountValue, { style: 'currency', currency: 'USD', minimumFractionDigits: 0 === discountValue % 1 ? 0 : 2, maximumFractionDigits: 0 === discountValue % 1 ? 0 : 2 }),
                  credit: intl.formatNumber(creditValue, { style: 'currency', currency: 'USD', minimumFractionDigits: 0 === creditValue % 1 ? 0 : 2, maximumFractionDigits: 0 === creditValue % 1 ? 0 : 2 })
                }}
              />
            </div>
            <div className="heavy margin-md-bottom text-xs-center text-sm-left">
              <FormattedMessage id='referrals.referrals_main.your_link' defaultMessage="Your referral link" />
            </div>
            <div className="margin-xl-bottom flex">
              { !entitlementError ?
                <InviteShareBtn
                  link={`/claim/${entitlementId}`}
                  title={ intl.formatMessage({ id: 'referrals.referrals_main.share_title', defaultMessage: 'Copy' }) }
                  actions={{
                    click: () => analyticsIntegrations.referralLinkCopied(SSOid)
                  }}
                  className="full-width"
                  inputContainerClassName="col-xs-9 no-gutter-left"
                  btnContainerClassName="col-xs-3 no-gutter"
                  visibleMobile
                  btnSize="full-width"
                  inputClassName="ellipsis"
                /> :
                <p className="error text-danger text-small">
                  <FormattedMessage id='referrals.referrals_main.share_error' defaultMessage="Something went wrong, please try again." />
                </p> }
            </div>
            <div className="bg-gray-100 text-gray-600 text-small rounded-border margin-xl-bottom" style={{ padding: 24 }}>
              <FormattedMessage
                id='referrals.referrals_main.new_users'
                defaultMessage="This offer is only valid for new Peerspace users and bookings over {subtotal}."
                values={{
                  subtotal: intl.formatNumber(minimumBookingSubtotal, { style: 'currency', currency: 'USD', minimumFractionDigits: 0 === minimumBookingSubtotal % 1 ? 0 : 2, maximumFractionDigits: 0 === minimumBookingSubtotal % 1 ? 0 : 2 })
                }}
              />
            </div>
            <div className="rounded-border margin-xl-bottom" style={{ padding: 24, border: '1px solid #DDE0E6' }}>
              <div className="h5 no-top-margin">
                <FormattedMessage id='referrals.referrals_main.earnings' defaultMessage="Earnings" />
              </div>
              <div className="flex space-between text-gray-600">
                <div>
                  <FormattedMessage id='referrals.referrals_main.referrals' defaultMessage="Referrals" />
                </div>
                <div>{referralCount}</div>
              </div>
              <hr className="margin-md-top margin-md-bottom"/>
              <div className="flex space-between heavy">
                <div>
                  <FormattedMessage id='referrals.referrals_main.credit_earned' defaultMessage="Credit earned" />
                </div>
                <div>
                  <FormattedNumber
                    value={parseFloat(referralBalanceAmount)}
                    currency='USD'
                    style="currency"
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                </div>
              </div>
            </div>
            { referralBalanceError &&
            <p className="error text-danger text-small">
              <FormattedMessage id='referrals.referrals_main.balance_error' defaultMessage="Something went wrong, please try again." />
            </p>
            }
            <p>
              <FormattedMessage
                id='referrals.referrals_main.current_balance'
                defaultMessage="To see your current credit balance, visit your {payments} page."
                values={{
                  payments: (
                    <a href="/account/methods">
                      <FormattedMessage id='referrals.referrals_main.payments' defaultMessage="payments" />
                    </a>
                  )
                }}
              />
            </p>
          </div>
        </div>
      </div>
      <div
        className="bg-gray-100 ps-container-lg-margin ps-container-fixed"
        style={{ paddingTop: '60px', paddingBottom: '60px' }}
      >
        <div className="ps-row">
          {
            map(howItWorksData, (section) => {
              const { icons, header, body, link } = section;
              return (
                <div className="col-xs-12 col-lg-4 text-center margin-xl-bottom">
                  <div>
                    { map(icons, (i) => {
                      return (
                        <img src={`https://assets.peerspace.com/${i}-gray.svg?v=rb`} className="margin-md-left margin-md-right" alt={`${i}-icon`} />
                      )
                    })}
                  </div>
                  <div className="h5">{header}</div>
                  <div className="text-gray-600 margin-md-bottom">{body}</div>
                  { link &&
                  <a href={`https://support.peerspace.com/hc/${zendeskLocale}/articles/360057498172-How-does-the-guest-referral-program-work-`} target="_blank">
                    <FormattedMessage id='referrals.referrals_main.learn_more' defaultMessage="Learn More ›" />
                  </a>
                  }
                </div>
              )
            })
          }
          <div className="text-gray-600 col-xs-12 margin-xl-top text-small">
            <FormattedMessage
              id='referrals.referrals_main.paid_in_peerspace_credit'
              defaultMessage="Earnings will be paid in Peerspace Credits which will be added directly to your Peerspace account. Peerspace Credits can only be used to book spaces in the United States. Earnings from your referral link will be capped at {value}. {terms}."
              values={{
                terms: (
                  <a href={`${urlPrefix}/legal/terms`} target="_blank">
                    <FormattedMessage id='referrals.referrals_main.terms_apply' defaultMessage="Terms and conditions apply" />
                  </a>
                ),
                value: intl.formatNumber(5000, { style: 'currency', currency: 'USD', minimumFractionDigits: 0 === 5000 % 1 ? 0 : 2, maximumFractionDigits: 0 === 5000 % 1 ? 0 : 2 })
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

ReferralsMain.propTypes = {
  referralCount: PropTypes.number,
  referralBalanceAmount: PropTypes.number,
  referralBalanceError: PropTypes.string,
  entitlementId: PropTypes.string,
  entitlementError: PropTypes.object,
  SSOid: PropTypes.string,
  entitlementDescription: PropTypes.string,
  creditValue: PropTypes.string,
  discountValue: PropTypes.number,
  minimumBookingSubtotal: PropTypes.number,
  intl: intlShape
};

export default injectIntl(ReferralsMain);
